import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { Section } from "./fields";
import { useAuth } from "../context/AuthContext";
import { Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const lastMonday = () => {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) - 7);
  return d.toISOString().slice(0, 10);
};

export default function AiSummarySection({ studentId }) {
  const [summaries, setSummaries] = useState([]);
  const [weekStart, setWeekStart] = useState(lastMonday());
  const [generating, setGenerating] = useState(false);
  const { user } = useAuth();
  const canApprove = ["coordinator", "supervisor", "tag_admin"].includes(user?.role);

  const load = () => { api.get("/ai/weekly-summaries", { params: { student_id: studentId } }).then((r) => setSummaries(r.data)).catch(() => {}); };
  useEffect(load, [studentId]);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/ai/weekly-summary", { student_id: studentId, week_start: weekStart });
      toast.success("Draft summary generated — review before use");
      load();
    } catch (e) { toast.error(apiError(e)); }
    setGenerating(false);
  };

  const approve = async (id) => {
    try { await api.put(`/ai/weekly-summaries/${id}/approve`); toast.success("Summary approved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Section
      title="AI Weekly Summary"
      action={
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="summary-week-input" type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
            className="rounded-md border border-slate-200 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-emerald-600" />
          <button data-testid="generate-summary-btn" onClick={generate} disabled={generating}
            className="flex items-center gap-1.5 text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 px-4 py-2.5 rounded-full transition-colors duration-150 active:scale-95 disabled:opacity-60">
            <Sparkles size={14} /> {generating ? "Generating…" : "Generate draft"}
          </button>
        </div>
      }
    >
      <p className="text-xs text-slate-400 mb-4">Drafted by AI from the week's documented entries. It organizes what staff documented — always review and approve before any formal use.</p>
      {summaries.length === 0 && <p className="text-sm text-slate-400">No summaries yet. Pick the week start (Monday) and generate a draft.</p>}
      <div className="space-y-3">
        {summaries.map((s) => (
          <div key={s.id} data-testid={`ai-summary-${s.id}`} className="border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <p className="text-sm font-semibold text-slate-800">Week of {s.week_start}</p>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
                {s.status === "approved" ? `Approved by ${s.approved_by}` : "Draft — needs review"}
              </span>
              <span className="text-xs text-slate-400">from {s.source_counts?.entries ?? 0} entries · {s.source_counts?.incidents ?? 0} incidents</span>
              {s.status === "draft" && canApprove && (
                <button data-testid={`approve-summary-btn-${s.id}`} onClick={() => approve(s.id)}
                  className="ml-auto flex items-center gap-1.5 text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3.5 py-2 rounded-full transition-colors duration-150">
                  <CheckCircle2 size={14} /> Approve
                </button>
              )}
            </div>
            <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{s.content}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}
